import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /**
   * Cercevo (2026-08-16): timer used to re-measure the enclosing header
   * mega-menu submenu after this accordion's open/closed state settles, so
   * desktop nested (3rd level) menu accordions don't get visually clipped
   * by the header's own submenu-height/clip-path animation. No-op outside
   * of a header mega-menu context (see #syncSubmenuHeight below).
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #submenuSyncTimer;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    clearTimeout(this.#submenuSyncTimer);
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }

  /**
   * Cercevo (2026-08-16): fired whenever this accordion's <details> opens or
   * closes. When the accordion lives inside a desktop header mega-menu
   * submenu (.menu-list__submenu), the enclosing <header-menu> component
   * measures the submenu's height once when it is first activated and uses
   * that value to drive a CSS clip-path while it animates open. If a user
   * later expands a nested accordion (e.g. "Stiller ve Temalar"), the
   * submenu grows taller than that original measurement and the new content
   * would be clipped off. Re-measuring and re-publishing the same CSS
   * variables the header menu relies on keeps the panel correctly sized.
   * This is a no-op anywhere else in the theme (facets, filters, etc.)
   * since those don't live inside a .menu-list__submenu ancestor.
   */
  #handleToggle = () => {
    this.#syncSubmenuHeight();

    // The details content animates its block-size from 0 to auto, so the
    // offsetHeight read immediately on toggle can be mid-transition. Re-sync
    // once more after the animation settles to catch the final height.
    clearTimeout(this.#submenuSyncTimer);
    this.#submenuSyncTimer = setTimeout(() => this.#syncSubmenuHeight(), 400);
  };

  #syncSubmenuHeight() {
    const submenu = this.closest('.menu-list__submenu');
    if (!(submenu instanceof HTMLElement)) return;

    const headerComponent = this.closest('header-component');
    if (!(headerComponent instanceof HTMLElement)) return;

    const isOverlap = headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? headerComponent.querySelector('.header__row--top') : headerComponent;
    const headerVisibleHeight = boundary instanceof HTMLElement ? boundary.getBoundingClientRect().height : 0;
    const submenuHeight = submenu.offsetHeight;

    headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    headerComponent.style.setProperty('--full-open-header-height', `${submenuHeight + headerVisibleHeight}px`);
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
